<template>
  <ui-side-modal v-show="show"
                 class="geo-content-insert-modal"
                 :width="860"
                 @close="handleClose">
    <div class="content-editor">
      <div class="editor-top">
        <h4 class="title">{{ isEditMode ? 'GEO 콘텐츠 수정' : 'GEO 콘텐츠 추가' }}</h4>
        <div class="actions">
          <ui-button :color="'gray'"
                     @click="handleClose">
            <i class="xi-close"></i> 취소
          </ui-button>
          <ui-button v-if="isEditMode"
                     :color="'red'"
                     @click="deleteContent">
            <i class="xi-trash"></i> 삭제
          </ui-button>
          <ui-button v-if="isReadonly"
                     :color="'yellow'"
                     @click="enableEdit">
            <i class="xi-pen"></i> 수정
          </ui-button>
          <ui-button v-else
                     :color="'pink'"
                     @click="save">
            <i class="xi-check"></i> 저장
          </ui-button>
        </div>
      </div>

      <div class="editor-content">
        <section class="section">
          <h5>콘텐츠 정보</h5>
          <div class="grid">
            <div class="row">
              <label>종류</label>
              <div class="value">
                <select v-model="form.type"
                        :disabled="isReadonly">
                  <option value="cxTrends">CX Trends</option>
                  <option value="whitePaper">White Paper</option>
                </select>
                <p v-if="errors.type"
                   class="error">{{ errors.type }}</p>
              </div>
            </div>
            <div class="row">
              <label>언어</label>
              <div class="value">
                <select v-model="form.lang"
                        :disabled="isReadonly">
                  <option value="ko">KO</option>
                  <option value="en">EN</option>
                  <option value="ja">JA</option>
                </select>
                <p v-if="errors.lang"
                   class="error">{{ errors.lang }}</p>
              </div>
            </div>
            <div class="row">
              <label>{{ form.type === 'whitePaper' ? 'Entity' : 'Mini' }}</label>
              <div class="value inline-value">
                <label class="checkbox-label">
                  <input v-model="form.isMini"
                         type="checkbox"
                         :disabled="isReadonly" />
                  <span>{{ form.type === 'whitePaper' ? 'entity' : 'mini' }}</span>
                </label>
              </div>
            </div>
            <div class="row">
              <label>Vol</label>
              <div class="value">
                <input v-model.number="form.vol"
                       min="1"
                       type="number"
                       :disabled="isReadonly" />
                <p v-if="errors.vol"
                   class="error">{{ errors.vol }}</p>
              </div>
            </div>
            <div class="row">
              <label>등록일자</label>
              <div class="value">
                <input v-model="form.regDate"
                       type="date"
                       :disabled="isReadonly" />
                <p v-if="errors.regDate"
                   class="error">{{ errors.regDate }}</p>
              </div>
            </div>
          </div>
        </section>

        <section class="section">
          <h5>파일</h5>
          <div class="grid">
            <div class="row">
              <label>HTML</label>
              <div class="value file-row">
                <input ref="htmlFile"
                       type="file"
                       accept=".html,text/html"
                       :disabled="isReadonly"
                       @change="setFile($event, 'htmlFile')" />
                <p v-if="form.htmlFile"
                   class="file-name">{{ form.htmlFile.name }}</p>
                <p v-else-if="isEditMode && form.reportS3Path"
                   class="file-name">현재: {{ form.reportS3Path }}</p>
                <p v-if="errors.htmlFile"
                   class="error">{{ errors.htmlFile }}</p>
              </div>
            </div>
            <div v-if="!form.isMini"
                 class="row">
              <label>PDF</label>
              <div class="value file-row">
                <input ref="pdfFile"
                       type="file"
                       accept=".pdf,application/pdf"
                       :disabled="isReadonly"
                       @change="setFile($event, 'pdfFile')" />
                <p v-if="form.pdfFile"
                   class="file-name">{{ form.pdfFile.name }}</p>
                <p v-else-if="isEditMode && form.pdfS3Path"
                   class="file-name">현재: {{ form.pdfS3Path }}</p>
                <p v-if="errors.pdfFile"
                   class="error">{{ errors.pdfFile }}</p>
              </div>
            </div>
          </div>
        </section>

        <section class="section">
          <h5>노출 문구</h5>
          <div class="grid single">
            <div class="row">
              <label>Title</label>
              <div class="value">
                <textarea v-model="form.title"
                          rows="4"
                          :disabled="isReadonly"></textarea>
                <p v-if="errors.title"
                   class="error">{{ errors.title }}</p>
              </div>
            </div>
            <div class="row">
              <label>Banner Title</label>
              <div class="value">
                <textarea v-model="form.bannerTitle"
                          rows="4"
                          :disabled="isReadonly"></textarea>
              </div>
            </div>
          </div>
        </section>

        <section class="section">
          <h5>리스트</h5>
          <div class="list-grid">
            <div class="list-box">
              <div class="list-title">
                <label>Desc</label>
                <button :disabled="isReadonly"
                        @click="addListItem('desc')">
                  <i class="xi-plus"></i>
                </button>
              </div>
              <div v-for="(item, index) in form.desc"
                   :key="`desc-${index}`"
                   class="list-item">
                <input v-model="form.desc[index]"
                       type="text"
                       :disabled="isReadonly" />
                <button :disabled="isReadonly"
                        @click="removeListItem('desc', index)">
                  <i class="xi-minus"></i>
                </button>
              </div>
              <p v-if="errors.desc"
                 class="error">{{ errors.desc }}</p>
            </div>
            <div class="list-box">
              <div class="list-title">
                <label>Tag List</label>
                <button :disabled="isReadonly"
                        @click="addListItem('tagList')">
                  <i class="xi-plus"></i>
                </button>
              </div>
              <div v-for="(item, index) in form.tagList"
                   :key="`tag-${index}`"
                   class="list-item">
                <input v-model="form.tagList[index]"
                       type="text"
                       :disabled="isReadonly" />
                <button :disabled="isReadonly"
                        @click="removeListItem('tagList', index)">
                  <i class="xi-minus"></i>
                </button>
              </div>
              <p v-if="errors.tagList"
                 class="error">{{ errors.tagList }}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  </ui-side-modal>
</template>

<script>
import { EventBus } from '@/utils/event-bus'
import { alertError } from '@/utils/tools'
import { deleteGeoAiReportContent, insertGeoAiReportContent, updateGeoAiReportContent } from '@/api/mine'

import UiButton from '@/components/_ui/UiButton'
import UiSideModal from '@/components/_ui/UiSideModal'

const getTodayDateString = () => {
  const now = new Date()
  return formatDateString(now)
}

const formatDateString = (value) => {
  const now = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(now.getTime())) {
    return getTodayDateString()
  }
  const year = now.getFullYear()
  const month = `${now.getMonth() + 1}`.padStart(2, '0')
  const date = `${now.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${date}`
}

const createDefaultForm = () => ({
  aid: '',
  type: 'cxTrends',
  lang: 'ko',
  isMini: false,
  vol: '',
  regDate: getTodayDateString(),
  htmlFile: null,
  pdfFile: null,
  reportS3Path: '',
  pdfS3Path: '',
  title: '',
  bannerTitle: '',
  desc: [''],
  tagList: ['']
})

export default {
  name: 'GeoContentInsertModal',
  components: {
    UiButton,
    UiSideModal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'create'
    },
    report: {
      type: Object,
      default: null
    }
  },
  computed: {
    isEditMode () {
      return this.mode === 'edit'
    },
    isReadonly () {
      return this.isEditMode && !this.editEnabled
    }
  },
  watch: {
    show (value) {
      if (value) {
        this.initialize()
      }
    },
    'form.isMini' (value) {
      if (value) {
        this.form.pdfFile = null
        if (this.$refs.pdfFile) {
          this.$refs.pdfFile.value = ''
        }
        this.$delete(this.errors, 'pdfFile')
      }
    }
  },
  methods: {
    initialize () {
      this.form = this.isEditMode ? this.mapReportToForm(this.report) : createDefaultForm()
      this.editEnabled = !this.isEditMode
      this.errors = {}
      this.$nextTick(() => {
        if (this.$refs.htmlFile) {
          this.$refs.htmlFile.value = ''
        }
        if (this.$refs.pdfFile) {
          this.$refs.pdfFile.value = ''
        }
      })
    },
    mapReportToForm (report) {
      const form = createDefaultForm()
      if (!report) {
        return form
      }
      form.aid = report._id || report.id || ''
      form.type = report.type || 'cxTrends'
      form.lang = report.lang || 'ko'
      form.isMini = ['mini', 'entity'].includes(report.promptType)
      form.vol = report.vol || ''
      form.regDate = report.regDate ? formatDateString(report.regDate) : getTodayDateString()
      form.title = report.title || ''
      form.bannerTitle = report.bannerTitle || ''
      form.desc = Array.isArray(report.desc) ? [...report.desc] : report.desc ? [report.desc] : ['']
      form.tagList = Array.isArray(report.tagList) ? [...report.tagList] : report.tagList ? [report.tagList] : ['']
      form.reportS3Path = report.reportS3Path || ''
      form.pdfS3Path = report.pdfS3Path || ''
      return form
    },
    setFile (event, field) {
      if (this.isReadonly) {
        return
      }
      const file = event.target.files[0]
      this.form[field] = file || null
      this.$delete(this.errors, field)
    },
    addListItem (field) {
      if (this.isReadonly) {
        return
      }
      this.form[field].push('')
    },
    removeListItem (field, index) {
      if (this.isReadonly) {
        return
      }
      if (this.form[field].length === 1) {
        this.$set(this.form[field], 0, '')
        return
      }
      this.form[field].splice(index, 1)
    },
    cleanList (list) {
      return list.map((item) => `${item}`.trim()).filter(Boolean)
    },
    validate () {
      this.errors = {}
      if (!this.form.type) {
        this.$set(this.errors, 'type', '종류는 필수입니다.')
      }
      if (!['ko', 'en', 'ja'].includes(this.form.lang)) {
        this.$set(this.errors, 'lang', '언어를 선택하세요.')
      }
      if (!Number.isInteger(Number(this.form.vol)) || Number(this.form.vol) < 1) {
        this.$set(this.errors, 'vol', 'Vol은 1 이상의 숫자여야 합니다.')
      }
      if (!this.form.regDate) {
        this.$set(this.errors, 'regDate', '등록일자는 필수입니다.')
      }
      if (!this.form.htmlFile && (!this.isEditMode || !this.form.reportS3Path)) {
        this.$set(this.errors, 'htmlFile', 'HTML 파일은 필수입니다.')
      } else if (this.form.htmlFile && !this.form.htmlFile.name.toLowerCase().endsWith('.html')) {
        this.$set(this.errors, 'htmlFile', 'HTML 파일만 업로드할 수 있습니다.')
      }
      if (!this.form.isMini) {
        if (this.form.pdfFile && !this.form.pdfFile.name.toLowerCase().endsWith('.pdf')) {
          this.$set(this.errors, 'pdfFile', 'PDF 파일만 업로드할 수 있습니다.')
        }
      }
      if (!this.form.title.trim()) {
        this.$set(this.errors, 'title', 'Title은 필수입니다.')
      }
      if (this.cleanList(this.form.desc).length === 0) {
        this.$set(this.errors, 'desc', 'Desc는 1개 이상 입력해야 합니다.')
      }
      if (this.form.type === 'cxTrends' && this.cleanList(this.form.tagList).length === 0) {
        this.$set(this.errors, 'tagList', 'Tag는 1개 이상 입력해야 합니다.')
      }
      return Object.keys(this.errors).length === 0
    },
    buildPayload () {
      return {
        aid: this.form.aid,
        type: this.form.type,
        lang: this.form.lang,
        isMini: this.form.isMini,
        vol: Number(this.form.vol),
        regDate: this.form.regDate,
        title: this.form.title.trim(),
        bannerTitle: this.form.bannerTitle.trim(),
        desc: this.cleanList(this.form.desc),
        tagList: this.cleanList(this.form.tagList),
        htmlFile: this.form.htmlFile,
        pdfFile: this.form.isMini ? null : this.form.pdfFile
      }
    },
    async save () {
      if (this.isReadonly) {
        return
      }
      if (!this.validate()) {
        return
      }
      if (!confirm('정말로 저장하시겠습니까?')) {
        return
      }
      EventBus.$emit('onLoading', true)
      try {
        if (this.isEditMode) {
          await updateGeoAiReportContent(this.buildPayload())
        } else {
          await insertGeoAiReportContent(this.buildPayload())
        }
        this.$emit('saved')
      } catch (error) {
        alertError('GEO 콘텐츠 저장에 실패했습니다.', error)
      } finally {
        EventBus.$emit('onLoading', false)
      }
    },
    async deleteContent () {
      if (!this.form.aid) {
        alertError('GEO 콘텐츠 삭제에 실패했습니다.', 'aid가 없습니다.')
        return
      }
      if (!confirm('정말로 삭제하시겠습니까? S3 파일도 함께 삭제됩니다.')) {
        return
      }
      EventBus.$emit('onLoading', true)
      try {
        await deleteGeoAiReportContent({ aid: this.form.aid })
        this.$emit('deleted')
      } catch (error) {
        alertError('GEO 콘텐츠 삭제에 실패했습니다.', error)
      } finally {
        EventBus.$emit('onLoading', false)
      }
    },
    handleClose () {
      this.$emit('close')
    },
    enableEdit () {
      this.editEnabled = true
    }
  },
  data () {
    return {
      form: createDefaultForm(),
      errors: {},
      editEnabled: true
    }
  }
}
</script>

<style lang="scss" scoped>
.content-editor {
  .editor-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 28px 28px 18px;
    border-bottom: 1px solid $light;

    .title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
    }

    .actions {
      display: flex;
      flex-shrink: 0;
    }
  }

  .editor-content {
    padding: 24px 28px 36px;
  }

  .section {
    margin-bottom: 28px;

    h5 {
      margin: 0 0 14px;
      font-size: 14px;
      font-weight: 700;
      color: $dark;
    }
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px 18px;

    &.single {
      grid-template-columns: 1fr;
    }
  }

  .row {
    display: grid;
    grid-template-columns: 110px minmax(0, 1fr);
    gap: 10px;
    align-items: start;

    label {
      padding-top: 7px;
      color: $darkgray;
      font-size: 12px;
      font-weight: 700;
    }
  }

  .value {
    min-width: 0;

    input,
    select,
    textarea {
      width: 100%;
      min-height: 34px;
      padding: 6px 9px;
      border: 1px solid $light;
      border-radius: 4px;
      background-color: $white;
      font-size: 13px;
      line-height: 1.4;
    }

    textarea {
      resize: vertical;
    }
  }

  .inline-value {
    padding-top: 6px;
  }

  .checkbox-label {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding-top: 0 !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    color: $dark !important;

    input {
      width: auto;
      min-height: auto;
    }
  }

  .file-row input {
    padding: 5px;
  }

  .file-name {
    margin: 6px 0 0;
    color: $darkgray;
    font-size: 12px;
    word-break: break-all;
  }

  .list-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
  }

  .list-title,
  .list-item {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 32px;
    gap: 8px;
    align-items: center;
  }

  .list-title {
    margin-bottom: 8px;

    label {
      font-size: 12px;
      font-weight: 700;
      color: $darkgray;
    }
  }

  .list-item {
    margin-top: 8px;

    input {
      min-height: 34px;
      padding: 6px 9px;
      border: 1px solid $light;
      border-radius: 4px;
    }
  }

  .list-title button,
  .list-item button {
    width: 32px;
    height: 32px;
    border-radius: 4px;
    background-color: $light;
    color: $dark;
  }

  .error {
    margin: 6px 0 0;
    color: $red;
    font-size: 12px;
  }
}

@media (max-width: 720px) {
  .content-editor {
    .editor-top {
      align-items: flex-start;
      flex-direction: column;
    }

    .grid,
    .list-grid {
      grid-template-columns: 1fr;
    }

    .row {
      grid-template-columns: 1fr;
      gap: 4px;
    }
  }
}
</style>
